import { CTA, SEOWrapper } from '@r-and-a-shared-ui/common-ui';
import Image from 'next/image';
import styles from './ErrorPage.module.scss';
import { useErrorPage } from "./api/error-page-hook/useErrorPage";
import { deleteCookie, getCookie, getQueryParamByKey, getSSODomain, hasValue, redirectTo, VWR_REFERRER } from '@r-and-a-shared-ui/utils';
import { OauthQuery } from '@r-and-a-shared-ui/auth-client';
import { GoogleAnalytics, GoogleTagManager } from '@next/third-parties/google';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';
import { getMappedSSOConfig } from '@r-and-a-shared-ui/core-data-mapping';

export interface ErrorPageProps{
  pageCodename: string,
  defaultHeaderText: string,
  defaultTitleText: string,
  defaultSubtitleText: string,
  theOpenLogo: any,
  aigwoLogo: any,
  randaLogo: any,
}

export function ErrorPage({
  pageCodename,
  defaultHeaderText,
  defaultTitleText,
  defaultSubtitleText,
  theOpenLogo,
  aigwoLogo,
  randaLogo,
} : ErrorPageProps) {

  const { errorPageData, isLoading } = useErrorPage(pageCodename, defaultHeaderText, defaultTitleText, defaultSubtitleText);
  
  const { data: config } = api.config.ssoConfig.useQuery();
  const mappedConfig = getMappedSSOConfig(config);

  if (isLoading || !errorPageData) {
    return <div>Loading...</div>;
  }

  const { headerText, titleText, subtitleText, logos, sideImage } = errorPageData;

  const fallbackLogos = [
    { logoImageUrl: theOpenLogo, logoImageAlt: 'The Open Logo', logoLink: 'https://www.theopen.com/' },
    { logoImageUrl: aigwoLogo, logoImageAlt: 'AIGWO Logo', logoLink: 'https://www.aigwomensopen.com/' },
    { logoImageUrl: randaLogo, logoImageAlt: 'R&A Logo', logoLink: 'https://www.randa.org/' }
  ];

  const displayedLogos = logos && logos.length > 0 ? logos : fallbackLogos;

  const goBackPage = getQueryParamByKey(OauthQuery.REDIRECT_URI);
  const goBackAction = () => {
    if(hasValue(goBackPage)) {
      redirectTo(goBackPage);
      return;
    }
    const VWRreferrer = getCookie(VWR_REFERRER);
    if(VWRreferrer) {
      deleteCookie(VWR_REFERRER);
      redirectTo(decodeURIComponent(VWRreferrer));
      return;
    }
    if(document.referrer.includes(getSSODomain())){
      window.history.go(-2);
      return;
    }
    window.history.go(-1);
  };

  return (
    <div className={styles.full}>
      {errorPageData.seoData && (<SEOWrapper {...errorPageData.seoData} />)}
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <div className={styles.page}>
            <span className={styles.label}>{headerText}</span>
            <h1 className={styles.title}>{titleText}</h1>
            <p>{subtitleText}</p>
            <div className={styles.backButton}>
              <CTA label="Go Back" onClick={goBackAction} />
            </div>
            <div className={styles.logos}>
              {displayedLogos.map((logo, index) => (
                <Image
                  key={index}
                  priority
                  src={typeof logo.logoImageUrl === 'string' ? logo.logoImageUrl : logo.logoImageUrl.src}
                  alt={logo.logoImageAlt}
                  className={styles.logo}
                  onClick={() => window.location.href = logo.logoLink}
                  width={50}
                  height={50}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.banner}>
          <Image
            src={sideImage?.url || '/images/DefaultBanner.jpg'}
            alt={sideImage?.name || 'Default Banner'}
            width={sideImage?.width || 1920}
            height={sideImage?.height || 1080}
            className={styles.background}
          />
        </div>
      </div>
      <GoogleTagManager gtmId={mappedConfig?.analyticsTagConfig?.googleTagManagerId || ''} />
      <GoogleAnalytics gaId={mappedConfig?.analyticsTagConfig?.googleAnalyticsId || ''} />
    </div>
  );
}

export default ErrorPage;